import { useState, useEffect, useRef, useCallback } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

import { listCompanies } from '../../../api/crmApi';

/**
 * Debounced company search hook. Skips searches under 2 chars and aborts
 * stale requests when the query changes mid-flight.
 *
 * @param {string} query
 * @returns {{ results: Array, loading: boolean }}
 */
function useCompanySearch(query) {
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!query || query.trim().length < 2) {
			setResults([]);
			return undefined;
		}

		let cancelled = false;
		const timer = setTimeout(async () => {
			setLoading(true);
			try {
				const res = await listCompanies({ search: query.trim(), pageSize: 8 });
				if (!cancelled) setResults(res.companies ?? []);
			} catch {
				/* ignore — empty results signal to user */
			}
			if (!cancelled) setLoading(false);
		}, 250);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query]);

	return { results, loading };
}

const CompanyAutocomplete = ({ value, selectedCompany, onChange, onSelect, onClear }) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const anchorRef = useRef(null);

	const closeDropdown = useCallback(() => setShowDropdown(false), []);

	const { results, loading } = useCompanySearch(selectedCompany ? '' : value);

	useEffect(() => {
		if (results.length > 0 && !selectedCompany) setShowDropdown(true);
	}, [results, selectedCompany]);

	const handleInputChange = (e) => {
		const v = e.target.value;
		onChange(v);
		if (selectedCompany) onClear();
	};

	const handleSelect = (company) => {
		onSelect(company);
		setShowDropdown(false);
	};

	return (
		<div>
			<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
				Unternehmen *
			</label>
			<div className="relative" ref={anchorRef}>
				<TextInput
					value={value}
					onChange={handleInputChange}
					onFocus={() => results.length > 0 && !selectedCompany && setShowDropdown(true)}
					placeholder="Firmenname eingeben oder suchen ..."
					className={`pr-9 ${selectedCompany ? '!border-primary-300 !bg-primary-100/50' : ''}`}
					required
				/>
				{selectedCompany ? (
					<button
						type="button"
						onClick={onClear}
						className="absolute right-2 top-1/2 -translate-y-1/2 text-bgBase-800 hover:text-text text-xs"
					>
						&times;
					</button>
				) : (
					<BsBuilding className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bgBase-800 pointer-events-none" />
				)}
			</div>

			{selectedCompany && <p className="text-[11px] text-primary-600 mt-0.5">Verknüpft mit bestehendem Lead</p>}

			<Popover
				open={showDropdown && !selectedCompany}
				onClose={closeDropdown}
				anchorRef={anchorRef}
				align="stretch"
			>
				<Panel.Body padded={false}>
					{loading && <div className="px-3 py-2 text-xs text-bgBase-800">Suche ...</div>}
					{!loading && results.length === 0 && value.trim().length >= 2 && (
						<div className="px-3 py-3 text-xs text-bgBase-800 text-center">
							Kein bestehendes Unternehmen gefunden.
							<br />
							<span className="text-bgBase-800">Der Deal wird mit dem eingegebenen Namen erstellt.</span>
						</div>
					)}
					{results.map((c) => (
						<button
							key={c.id}
							type="button"
							onClick={() => handleSelect(c)}
							className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-bgBase-300 transition-colors border-b border-bgBase-400 last:border-b-0"
						>
							<BsBuilding className="w-3.5 h-3.5 text-bgBase-800 flex-shrink-0" />
							<span className="text-sm text-text truncate">{c.companyName}</span>
						</button>
					))}
					{!loading && results.length > 0 && value.trim().length >= 2 && (
						<div className="border-t border-bgBase-400 px-3 py-2 text-[11px] text-bgBase-800 text-center">
							Oder einfach weitermachen — der eingegebene Name wird verwendet.
						</div>
					)}
				</Panel.Body>
			</Popover>
		</div>
	);
};

CompanyAutocomplete.propTypes = {
	value: PropTypes.string.isRequired,
	selectedCompany: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onSelect: PropTypes.func.isRequired,
	onClear: PropTypes.func.isRequired,
};

export default CompanyAutocomplete;
